import { safeSessionStorage } from './storageUtils';

const SCROLL_BEFORE_AUTH_STORAGE_KEY = 'search-modules:scroll-before-auth';

type StoredAuthScroll = {
  y: number;
  path: string;
};

let savedScroll: StoredAuthScroll | null = null;
let unlockTimer: ReturnType<typeof setTimeout> | null = null;
let lockHandler: (() => void) | null = null;
let hasAttemptedPersistedRestore = false;

const getCurrentPath = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

const persistScrollPosition = (payload: StoredAuthScroll): void => {
  safeSessionStorage.setItem(SCROLL_BEFORE_AUTH_STORAGE_KEY, JSON.stringify(payload));
};

const readPersistedScrollPosition = (): StoredAuthScroll | null => {
  const raw = safeSessionStorage.getItem(SCROLL_BEFORE_AUTH_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredAuthScroll;
    if (typeof parsed.y !== 'number' || typeof parsed.path !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearPersistedScrollPosition = (): void => {
  safeSessionStorage.removeItem(SCROLL_BEFORE_AUTH_STORAGE_KEY);
};

const resolveSavedScrollY = (): number | null => {
  const currentPath = getCurrentPath();
  const candidate = savedScroll ?? readPersistedScrollPosition();
  // Reason: OAuth returns to the same page; ignore stale values from a different route.
  if (!candidate || candidate.path !== currentPath) return null;
  return candidate.y;
};

const clearScrollLock = (): void => {
  if (typeof window === 'undefined') return;

  if (lockHandler) {
    window.removeEventListener('scroll', lockHandler);
    lockHandler = null;
  }

  if (unlockTimer) {
    clearTimeout(unlockTimer);
    unlockTimer = null;
  }
};

export const captureScrollForAuth = (): void => {
  if (typeof window === 'undefined') return;
  if (lockHandler) return;
  if (!hasAttemptedPersistedRestore) {
    const persisted = readPersistedScrollPosition();
    if (persisted?.path === getCurrentPath()) return;
  }
  const payload: StoredAuthScroll = { y: window.scrollY, path: getCurrentPath() };
  savedScroll = payload;
  persistScrollPosition(payload);
};

export const restorePersistedAuthScrollIfPresent = (holdMs = 1000): boolean => {
  hasAttemptedPersistedRestore = true;
  if (resolveSavedScrollY() == null) return false;
  restoreScrollAfterAuth(holdMs);
  return true;
};

export const restoreScrollAfterAuth = (holdMs = 1000): void => {
  if (typeof window === 'undefined') return;

  hasAttemptedPersistedRestore = true;
  const scrollY = resolveSavedScrollY() ?? window.scrollY;
  savedScroll = { y: scrollY, path: getCurrentPath() };
  clearScrollLock();

  const restore = (): void => {
    if (window.scrollY !== scrollY) {
      window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
    }
  };

  lockHandler = () => {
    window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
  };
  window.addEventListener('scroll', lockHandler);

  restore();

  unlockTimer = setTimeout(() => {
    clearScrollLock();
    savedScroll = null;
    clearPersistedScrollPosition();
  }, holdMs);
};
